import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import moment from 'moment';
import config from '../../../config';
import Api from '../../../utils/api';
import useFetchData from '../../../hooks/useFetchData';
import { UploadBasic } from 'components/Upload';
import { ReactComponent as PDFIcon } from '../../../assets/images/pdf.svg';
import { Delete } from '@mui/icons-material';
import BasicDialog from 'components/Dialog';
import './ClientProductFiles.scss';

const uploadClientProductFile = (clientProductUuid, file, fileName) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('clientProductUuid', clientProductUuid);
  formData.append('fileName', fileName);

  return Api.post('/client-product/file/uploadClientProductFile', formData);
};

const deleteClientProductFile = (clientProductUuid, fileId) => {
  return Api.delete(
    `/client-product/file/clientProduct/${clientProductUuid}/${fileId}`
  );
};

const ClientProductFiles = ({ clientProductId, clientProductUuid }) => {
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  const {
    data: clientProductFiles = [],
    loading: isLoadingFiles,
    refetch,
  } = useFetchData(
    'client-product/file/clientProduct',
    clientProductUuid,
    'get'
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      customFileName: '',
    },
  });

  const handleSelectFile = (file) => {
    // Validate PDF file type
    if (file.type !== 'application/pdf') {
      alert('יש להעלות קובץ PDF בלבד');
      return;
    }
    setSelectedFile(file);
    const fileName = file.name.replace(/\.pdf$/i, '');
    reset({ customFileName: fileName });
  };

  const customFileName = watch('customFileName');

  const onUploadSubmit = (data) => {
    if (!selectedFile) {
      alert('בחר קובץ להעלאה');
      return;
    }

    setUploadingFile(true);
    uploadClientProductFile(
      clientProductUuid,
      selectedFile,
      data.customFileName || selectedFile.name
    )
      .then((res) => {
        if (res?.data) {
          // Refresh files list
          refetch();
          setShowFileUpload(false);
          setSelectedFile(null);
          reset();
        }
      })
      .catch((err) => {
        alert('שגיאה בהעלאת הקובץ');
        console.log('Error uploading file:', err);
      })
      .finally(() => {
        setUploadingFile(false);
      });
  };

  const handleDeleteFile = (fileId) => {
    setFileToDelete(fileId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (isDeletingFile) return;
    if (!fileToDelete) return;

    setIsDeletingFile(true);
    deleteClientProductFile(clientProductUuid, fileToDelete)
      .then((res) => {
        refetch();
      })
      .catch((err) => {
        alert('שגיאה במחיקת הקובץ');
        console.log('Error deleting file:', err);
      })
      .finally(() => {
        setIsDeletingFile(false);
        setShowDeleteConfirm(false);
        setFileToDelete(null);
      });
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteConfirm(false);
    setFileToDelete(null);
  };

  return (
    <div className="client-product-files-section">
      <div className="files-section-header">
        <h3>קבצים מצורפים</h3>
        <button
          className="upload-files-btn"
          onClick={() => setShowFileUpload(!showFileUpload)}
        >
          {showFileUpload ? 'ביטול' : '+ הוסף קובץ'}
        </button>
      </div>

      {showFileUpload && (
        <div className="file-upload-container">
          <div className="file-upload-input">
            <UploadBasic
              handleSaveFile={handleSelectFile}
              isLoading={uploadingFile}
              fileType="application/pdf"
            />
            <span>{selectedFile?.name || 'בחר קובץ PDF להעלאה'}</span>
          </div>
          {selectedFile && (
            <form onSubmit={handleSubmit(onUploadSubmit)}>
              <div className="file-name-input-container">
                <label htmlFor="custom-file-name">שם הקובץ:</label>
                <div className="file-name-input-wrapper">
                  <input
                    id="custom-file-name"
                    type="text"
                    placeholder="הזן שם קובץ"
                    className="file-name-input"
                    {...register('customFileName', {
                      required: 'שם הקובץ חובה',
                      minLength: {
                        value: 1,
                        message: 'שם הקובץ לא יכול להיות ריק',
                      },
                      maxLength: {
                        value: 255,
                        message: 'שם הקובץ ארוך מדי (עד 255 תווים)',
                      },
                      pattern: {
                        value: /^[^/\\:*?"<>|]+$/,
                        message:
                          'שם הקובץ מכיל תווים לא חוקיים: / \\ : * ? " < > |',
                      },
                    })}
                  />
                  <span className="file-extension">.pdf</span>
                </div>
                {errors.customFileName && (
                  <div className="file-name-error">
                    {errors.customFileName.message}
                  </div>
                )}
                <div className="upload-actions">
                  <button
                    type="submit"
                    className="upload-confirm-btn"
                    disabled={uploadingFile || !customFileName.trim()}
                  >
                    {uploadingFile ? 'בהעלאה...' : 'העלה'}
                  </button>
                  <button
                    type="button"
                    className="upload-cancel-btn"
                    onClick={() => {
                      setSelectedFile(null);
                      reset();
                    }}
                    disabled={uploadingFile}
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      )}

      {isLoadingFiles && <div className="loading-message">טוען קבצים...</div>}

      {!isLoadingFiles && clientProductFiles && clientProductFiles.length > 0 && (
        <div className="files-list-container">
          <table className="files-table">
            <thead>
              <tr>
                <th>שם קובץ</th>
                <th>תאריך הוספה</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {clientProductFiles.map((file, index) => (
                <tr key={index} className="file-row">
                  <td>{file.fileName}</td>
                  <td>
                    {file.createdAt
                      ? moment(file.createdAt).format('DD.MM.YYYY')
                      : '-'}
                  </td>
                  <td className="file-actions">
                    {file.id && (
                      <>
                        <a
                          href={`${config.url}api/client-product/file/clientProduct/${clientProductUuid}/${file.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-link"
                        >
                          <PDFIcon className="pdf-icon" />
                        </a>
                        <button
                          onClick={() => handleDeleteFile(file.id)}
                          className="delete-file-btn"
                        >
                          <Delete className="delete-icon" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoadingFiles &&
        clientProductFiles &&
        clientProductFiles.length === 0 &&
        !showFileUpload && (
          <div className="no-files-message">אין קבצים מצורפים</div>
        )}

      {showDeleteConfirm && (
        <BasicDialog
          title="מחיקת קובץ"
          isActionsDisabled={isDeletingFile}
          description={
            isDeletingFile
              ? 'מוחק את הקובץ...'
              : 'האם אתה בטוח שברצונך למחוק את הקובץ?'
          }
          handleSubmit={handleConfirmDelete}
          handleClose={isDeletingFile ? () => {} : handleCloseDeleteDialog}
        />
      )}
    </div>
  );
};

ClientProductFiles.propTypes = {
  clientProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientProductUuid: PropTypes.string.isRequired,
};

export default ClientProductFiles;
